import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Paintbrush2, Inbox, ExternalLink, Mail, Home } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/admin/StatusBadge";

const STATUS_OPTS = [
  { value: "",          label: "All leads" },
  { value: "new",       label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "quoted",    label: "Quoted" },
  { value: "won",       label: "Won" },
  { value: "lost",      label: "Lost" },
  { value: "spam",      label: "Spam" },
];

const NEXT_STATUSES = ["new", "contacted", "quoted", "won", "lost", "spam"];

/**
 * Gate 2 · S4 (2026-02) — Leads is the painter's unified inbox.
 * Two sources merged server-side and discriminated by the `source` field:
 *   • "enquiry"          — quote_requests (existing flow)
 *   • "homeowner_share"  — pro_cms_share_links, direction=homeowner_to_painter
 * Homeowner-shared rows carry a `share_token` used to open the read-only
 * visualisation viewer at /pro/leads/shared/{token}.
 */
export default function PainterLeads() {
  const { refreshMe } = useOutletContext();
  const nav = useNavigate();
  const [filter, setFilter] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [snapshotImageUrl, setSnapshotImageUrl] = useState(null);

  // Auth-gated hero image (blob) — works for both lead sources.
  useEffect(() => {
    let revoke = null;
    if (selected?.project_snapshot?.hero_render_id) {
      api.get(`/painter/leads/${selected.id}/snapshot-image`, { responseType: "blob" })
        .then((r) => {
          const url = URL.createObjectURL(r.data);
          revoke = url;
          setSnapshotImageUrl(url);
        })
        .catch(() => setSnapshotImageUrl(null));
    } else {
      setSnapshotImageUrl(null);
    }
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [selected?.id, selected?.project_snapshot?.hero_render_id]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/painter/leads", { params: { status: filter || undefined, page_size: 50 } });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      if (e?.response?.status === 404) { setItems([]); setTotal(0); }
      else { toast.error(formatApiError(e)); }
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (lead, next) => {
    try {
      await api.patch(`/painter/leads/${lead.id}`, { painter_lead_status: next });
      await load();
      await refreshMe();
      toast.success(`Marked as ${next}`);
      setSelected(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="painter-leads" className="space-y-8">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline text-brand-teal mb-2">ColourMatch Pro</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-brand-ink">Leads</h1>
          <p className="text-sm text-brand-muted mt-2 max-w-xl leading-relaxed">
            Enquiries from your ColourMatch profile and projects homeowners have shared with you directly.
            Convert leads into quotes and jobs without leaving this page.
          </p>
        </div>
        <div className="shrink-0 text-xs text-brand-muted" data-testid="leads-count">
          {total} {total === 1 ? "lead" : "leads"}
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2" data-testid="leads-filter-pills">
        {STATUS_OPTS.map((s) => {
          const active = filter === s.value;
          return (
            <button
              key={s.value || "all"}
              onClick={() => setFilter(s.value)}
              data-testid={`leads-filter-${s.value || "all"}`}
              className={`inline-flex items-center h-8 px-3.5 text-xs rounded-full border transition-colors ${
                active
                  ? "bg-brand-navy text-white border-brand-navy"
                  : "bg-white text-brand-ink border-brand-linen hover:border-brand-teal hover:bg-brand-linen/30"
              }`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Lead list */}
      <div className="border border-brand-linen bg-white rounded-2xl overflow-hidden shadow-soft" data-testid="leads-list">
        {loading && (
          <div className="p-6 text-sm text-brand-muted flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand-teal animate-pulse" />
            Loading leads…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-10 lg:p-14 text-center" data-testid="leads-empty">
            <div className="w-12 h-12 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center mx-auto mb-4">
              <Inbox className="w-5 h-5" />
            </div>
            <h3 className="font-serif text-xl text-brand-ink mb-2">
              {filter ? `No ${filter} leads yet` : "No leads yet"}
            </h3>
            <p className="text-sm text-brand-muted max-w-md mx-auto leading-relaxed">
              {filter
                ? "Try a different status filter above, or clear it to see everyone."
                : "When a homeowner enquires through your ColourMatch profile or shares a visualisation with you, it'll appear here — with the exact photo, colours and notes they designed."}
            </p>
          </div>
        )}
        {!loading && items.length > 0 && (
          <ul className="divide-y divide-brand-linen">
            {items.map((l) => {
              const isShared = l.source === "homeowner_share";
              return (
                <li
                  key={l.id}
                  className="p-5 cursor-pointer hover:bg-brand-linen/25 transition-colors"
                  onClick={() => setSelected(l)}
                  data-testid={`lead-${l.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-brand-ink truncate">{l.full_name}</span>
                        {isShared && (
                          <span
                            data-testid={`lead-badge-shared-${l.id}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-brand-teal/10 text-brand-teal"
                          >
                            <Home size={9} /> Homeowner Shared
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-brand-muted mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span>{l.suburb || (isShared ? "Shared visualisation" : "No suburb")}</span>
                        <span className="text-brand-muted/40">·</span>
                        <span className="capitalize">{(l.project_scope || "").replace(/_/g, " ") || "—"}</span>
                        {!isShared && l.property_type && (
                          <>
                            <span className="text-brand-muted/40">·</span>
                            <span className="capitalize">{(l.property_type || "").replace(/_/g, " ")}</span>
                          </>
                        )}
                        <span className="text-brand-muted/40">·</span>
                        <span>{new Date(l.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
                      </div>
                    </div>
                    <StatusBadge status={l.painter_lead_status || "new"} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {selected && (
        <LeadDrawer
          lead={selected}
          snapshotImageUrl={snapshotImageUrl}
          onClose={() => setSelected(null)}
          onUpdateStatus={updateStatus}
          onCreateQuote={() => nav(`/pro/quotes/new?lead=${selected.id}`)}
          onOpenVisualisation={() => nav(`/pro/leads/shared/${selected.share_token}`)}
        />
      )}
    </div>
  );
}

function LeadDrawer({ lead, snapshotImageUrl, onClose, onUpdateStatus, onCreateQuote, onOpenVisualisation }) {
  const isShared = lead.source === "homeowner_share";
  const mailtoHref = lead.email ? `mailto:${encodeURIComponent(lead.email)}` : null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-6 overflow-y-auto"
      onClick={onClose} data-testid="lead-detail-drawer">
      <div className="bg-white max-w-xl w-full p-8 my-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-2">
          <div className="overline">Lead</div>
          {isShared && (
            <span
              data-testid="lead-drawer-badge-shared"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-brand-teal/10 text-brand-teal"
            >
              <Home size={9} /> Homeowner Shared
            </span>
          )}
        </div>
        <h2 className="font-serif text-2xl text-brand-ink mb-3">{lead.full_name}</h2>
        <div className="space-y-2 text-sm text-brand-ink">
          {lead.email && <div><span className="overline w-32 inline-block">Email</span> {lead.email}</div>}
          {lead.phone && <div><span className="overline w-32 inline-block">Phone</span> {lead.phone}</div>}
          {lead.address && <div><span className="overline w-32 inline-block">Address</span> {lead.address}</div>}
          {lead.project_scope && <div><span className="overline w-32 inline-block">Scope</span> {lead.project_scope}</div>}
          {!isShared && lead.property_type && <div><span className="overline w-32 inline-block">Property</span> {lead.property_type}</div>}
          {lead.notes && <div><span className="overline w-32 inline-block">Notes</span> {lead.notes}</div>}
          {isShared && (
            <div><span className="overline w-32 inline-block">Shared</span>{" "}
              {new Date(lead.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
            </div>
          )}
        </div>

        {/* Project snapshot — reused for both sources */}
        {lead.project_snapshot && (
          <div className="mt-6 pt-6 border-t border-brand-linen" data-testid="lead-project-snapshot">
            <div className="flex items-center gap-2 mb-3">
              <Paintbrush2 size={14} className="text-brand-teal" />
              <span className="overline">
                {isShared ? "Their shared visualisation" : "From their ColourMatch design"}
              </span>
            </div>
            {lead.project_snapshot.project_title && (
              <div className="text-sm font-medium text-brand-ink" data-testid="lead-snapshot-title">
                {lead.project_snapshot.project_title}
              </div>
            )}
            {lead.project_snapshot.scheme_name && (
              <div className="text-xs text-brand-muted mt-0.5">
                Scheme: <span className="text-brand-ink">{lead.project_snapshot.scheme_name}</span>
              </div>
            )}
            {snapshotImageUrl && (
              <img
                src={snapshotImageUrl}
                alt="Project preview"
                data-testid="lead-snapshot-image"
                className="mt-3 w-full max-h-72 object-cover border border-brand-linen"
              />
            )}
            {lead.project_snapshot.scheme_colours?.length > 0 && (
              <div className="mt-4 space-y-1.5" data-testid="lead-snapshot-colours">
                {lead.project_snapshot.scheme_colours.map((c, i) => (
                  <div key={`sc-${i}-${c.color_hex}`} className="flex items-center gap-2.5 text-xs">
                    <span
                      className="inline-block w-5 h-5 border border-brand-linen flex-shrink-0"
                      style={{ background: c.color_hex }}
                    />
                    <span className="font-medium text-brand-ink">{c.color_name || c.color_hex}</span>
                    <span className="text-brand-muted">
                      {[c.surface, c.finish].filter(Boolean).join(" · ").replace(/_/g, " ")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Status controls */}
        <div className="mt-6 pt-6 border-t border-brand-linen">
          <div className="overline mb-3">Mark as</div>
          <div className="flex flex-wrap gap-2">
            {NEXT_STATUSES.map((s) => (
              <button key={s} onClick={() => onUpdateStatus(lead, s)}
                data-testid={`lead-set-${s}`}
                className={`px-3 py-1.5 text-sm border ${lead.painter_lead_status === s ? "bg-brand-navy text-white border-brand-teal" : "border-brand-linen hover:bg-brand-linen/30"}`}>
                {s}
              </button>
            ))}
          </div>

          {/* Primary + secondary actions differ by source */}
          <div className="mt-5 pt-5 border-t border-brand-linen space-y-3">
            {isShared ? (
              <>
                <button
                  onClick={onOpenVisualisation}
                  data-testid="lead-open-visualisation"
                  className="w-full px-4 py-2.5 rounded-lg bg-brand-navy text-white font-semibold hover:bg-brand-navy/90 text-sm inline-flex items-center justify-center gap-2"
                >
                  <ExternalLink size={14} /> Open Visualisation
                </button>
                {mailtoHref && (
                  <a
                    href={mailtoHref}
                    data-testid="lead-reply-email"
                    className="w-full px-4 py-2.5 rounded-lg border border-brand-navy text-brand-navy font-semibold hover:bg-brand-sand text-sm inline-flex items-center justify-center gap-2"
                  >
                    <Mail size={14} /> Reply by email
                  </a>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={onCreateQuote}
                  data-testid="lead-create-quote"
                  className="w-full px-4 py-2.5 rounded-lg bg-brand-navy text-white font-semibold hover:bg-brand-navy/90 text-sm"
                >
                  + Create quote from this lead
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Opens the Quote Builder pre-filled with this lead&apos;s details.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
